import {useCallback, useEffect} from 'react'
import {onExperimentProgress, onExperimentStatus, subscribeToExperiment, unsubscribeFromExperiment} from '@/lib/services/websocket'
import useSWR from 'swr'
import type { QuestionnaireThreadDraftListItem } from '@digital-twins/shared-types'
import httpClient from '@/lib/api/http-client'
import { useAuth } from '@/lib/config/auth-provider'
import { reportStudyCardsLoadFailure } from '@/lib/utils/report-study-cards-load-failure'

export function threadStudyDraftsKey(userId: string, threadToken: string) {
  return ['thread-study-drafts', userId, threadToken] as const
}

/** Canonical row metadata only; study status remains server-owned. */
export function useThreadStudyDrafts(threadToken?: string) {
  const {isAuthenticated, user} = useAuth()
  const enabled = isAuthenticated && Boolean(user?.id) && Boolean(threadToken)
  const {data, error, isLoading, mutate} = useSWR(
    enabled ? threadStudyDraftsKey(user!.id, threadToken!) : null,
    async () => (await httpClient.get<{items: Array<QuestionnaireThreadDraftListItem>}>(
      '/questionnaire-drafts', {params: {sourceThreadToken: threadToken}},
    )).data,
    {
      refreshInterval: 15_000, dedupingInterval: 5_000, refreshWhenHidden: false, refreshWhenOffline: false,
      onError: (failure) => reportStudyCardsLoadFailure('study_drafts', threadToken!, failure),
    },
  )
  const refresh = useCallback(() => mutate(undefined, {revalidate: true}), [mutate])
  // The existing socket owner re-joins desired rooms after reconnect. Events
  // only invalidate this canonical projection; their payloads are not state.
  const experimentRooms = enabled ? Array.from(new Set((data?.items ?? []).flatMap((item) => item.launchedStudyToken ? [item.launchedStudyToken] : []))).sort().join(',') : ''
  useEffect(() => {
    if (!enabled || !experimentRooms) return
    const stop = experimentRooms.split(',').map((studyToken) => {
      const invalidate = () => {void mutate(undefined, {revalidate: true})}
      const offProgress = onExperimentProgress(studyToken, invalidate)
      const offStatus = onExperimentStatus(studyToken, invalidate)
      subscribeToExperiment(studyToken)
      return () => {offProgress(); offStatus(); unsubscribeFromExperiment(studyToken)}
    })
    return () => stop.forEach((unsubscribe) => unsubscribe())
  }, [enabled, experimentRooms, mutate])
  return {items: enabled ? data?.items ?? [] : [], isLoading: enabled && isLoading, error, refresh}
}
